import { useMemo } from "react"
import { useParams } from "react-router-dom"
import { useAuth } from "./useAuth"
import { useGetOrganizationsQuery } from "@/redux/services/organizationApi"
import { useGetAdminOrganizationsQuery } from "@/redux/services/adminOrganizationsApi"

// Constant for all possible application routes
const ALL_PAGES = [
  "/dashboard",
  "/campaigns",
  "/prospects",
  "/dnc",
  "/inbox",
  "/sequences",
  "/master-lists",
  "/search-criteria",
  "/domains",
  "/inboxing-domains",
  "/emails",
  "/senders",
  "/reply-classification",
  "/spam-testing",
]

const ADMIN_ONLY_PAGES = ["/inbox", "/spam-testing"]

const isAdminOnlyPath = (path = "") => {
  const normalized = path.toLowerCase().trim()
  const withoutLeadingSlash = normalized.replace(/^\/+/, "")

  return ADMIN_ONLY_PAGES.some((adminOnlyPage) => {
    const adminOnly = adminOnlyPage.replace(/^\/+/, "")
    return (
      withoutLeadingSlash === adminOnly ||
      withoutLeadingSlash.startsWith(`${adminOnly}/`)
    )
  })
}

const filterAdminOnlyPages = (pages: string[], isAdmin: boolean) =>
  isAdmin ? pages : pages.filter((page) => !isAdminOnlyPath(page))

const isSearchCriteriaPath = (path = "") => {
  const normalized = path.toLowerCase().trim().replace(/^\/+/, "")
  const pathParts = normalized.split("/").filter(Boolean)

  return pathParts.includes("search-criteria")
}

const filterSearchCriteriaPages = (
  pages: string[],
  searchCriteriaEnabled: boolean,
) =>
  searchCriteriaEnabled
    ? pages
    : pages.filter((page) => !isSearchCriteriaPath(page))

// Types for permission levels
export type PermissionLevel = "FULL" | "EDIT" | "READ" | "NONE"

/**
 * Interface for hook props
 */
interface UseOrganizationPermissionsProps {
  page?: string
}

/**
 * Interface for the return type of useOrganizationPermissions
 */
interface UseOrganizationPermissionsReturn {
  permissionLevel: PermissionLevel
  hasAccessToPage: boolean
  accessiblePages: string[]
  isLoading: boolean
  organization: any // Using any for simplicity, should be Organization type
  autoSelectedOrgId: string | null
  hasEditPermission: boolean
  hasPageAccess: boolean
  hasEditPermissionForPage: (pageName: string) => boolean
}

/**
 * Helper function to check if a user ID is in an array (handles string and object ID comparisons)
 */
const userIdInArray = (
  currentUserId: string | undefined,
  array: any[] = [],
) => {
  if (!array || !array.length || !currentUserId) return false

  // Convert currentUserId to string to ensure consistent comparison
  const userIdString = currentUserId.toString()

  return array.some((id) => {
    if (!id) return false

    // Handle different possible ID formats:
    // 1. Object with _id property (MongoDB document)
    // 2. ObjectId object
    // 3. String ID
    let arrayId: string

    if (typeof id === "object" && id !== null) {
      if (id._id) {
        // Case 1: MongoDB document with _id field
        arrayId =
          typeof id._id === "object" ? id._id.toString() : id._id.toString()
      } else {
        // Case 2: ObjectId object
        arrayId = id.toString()
      }
    } else {
      // Case 3: String ID
      arrayId = String(id)
    }

    // Remove any surrounding quotes that might be present (happens sometimes with string IDs)
    arrayId = arrayId.replace(/^["']|["']$/g, "")

    return arrayId === userIdString
  })
}

const canReadUserEditDnc = (
  organization: any,
  permissionLevel: PermissionLevel,
) => {
  if (!organization || permissionLevel !== "READ") return false

  const permissions = organization.specialPermissions
  if (!permissions) return false

  if (permissions.editDncList && permissions.editDncList.allowEdit === true) {
    return true
  }

  if (permissions.dncListEdit === true) {
    return true
  }

  return false
}

/**
 * Hook to check user permissions for the current organization
 *
 * @param props Optional props including the page to check permissions for
 * @returns Object with permission checks and utility functions
 */
export const useOrganizationPermissions = (
  props?: UseOrganizationPermissionsProps,
): UseOrganizationPermissionsReturn => {
  const { id: organizationId } = useParams<{ id: string }>()
  const { user: userData } = useAuth()
  const currentUserId = userData?._id
  const isAdmin = userData?.isAdmin === true
  const searchCriteriaEnabled =
    isAdmin || userData?.searchCriteriaAccess === true

  // Get organization data - using the appropriate API based on user type
  // Only admin users should call the admin API, regular users should use the standard API
  const { data: adminOrganizationsData } = useGetAdminOrganizationsQuery(
    { limit: 100 },
    { skip: !currentUserId || !isAdmin },
  )

  const { data: regularOrganizationsData } = useGetOrganizationsQuery(
    undefined,
    { skip: !currentUserId || isAdmin },
  )

  // Combine data sources based on user type
  const organizationsData = useMemo(() => {
    if (isAdmin && adminOrganizationsData) {
      return adminOrganizationsData
    }

    if (!isAdmin && regularOrganizationsData) {
      // Convert regular org data to same format as admin data if needed
      return {
        items: regularOrganizationsData,
        total: regularOrganizationsData.length,
      }
    }

    return { items: [], total: 0 }
  }, [isAdmin, adminOrganizationsData, regularOrganizationsData])

  // Auto-select first organization if none is selected but user has access to orgs
  const autoSelectedOrgId = useMemo(() => {
    // Only auto-select if we have orgs but no org is currently selected
    if (!organizationId && organizationsData?.items?.length > 0) {
      return organizationsData.items[0]._id
    }
    return null
  }, [organizationId, organizationsData])

  // Use either the URL param org ID or the auto-selected org ID
  const effectiveOrgId = organizationId || autoSelectedOrgId

  // Find the current organization
  const currentOrganization = useMemo(() => {
    if (!organizationsData?.items || !effectiveOrgId) return null

    // Use string comparison for IDs
    return organizationsData.items.find(
      (org) => org._id.toString() === effectiveOrgId.toString(),
    )
  }, [organizationsData, effectiveOrgId])

  // Determine the user's permission level for this organization
  const permissionLevel = useMemo((): PermissionLevel => {
    // If user is admin, they have full access
    if (isAdmin) return "FULL"

    // If we don't have organization data yet, return NONE
    if (!currentOrganization || !currentUserId) return "NONE"

    // For regular users with no organization context, return appropriate level
    if (!effectiveOrgId && userData) {
      return "READ" // Default to read for general navigation
    }

    // Check if user is the owner (if owner property exists)
    // The owner might be an object with _id or directly an ID string
    // @ts-ignore: Handle both AdminOrganization and basic Organization types
    const ownerField = currentOrganization.owner
    const ownerMatch =
      ownerField &&
      // If owner is an object with _id
      ((typeof ownerField === "object" &&
        ownerField._id &&
        ownerField._id.toString() === currentUserId.toString()) ||
        // If owner is a string ID
        (typeof ownerField === "string" &&
          ownerField.toString() === currentUserId.toString()))

    if (ownerMatch) return "FULL"

    // Check each permission level using the helper function
    if (userIdInArray(currentUserId, currentOrganization.fullAccessUsers))
      return "FULL"
    if (userIdInArray(currentUserId, currentOrganization.editAccessUsers))
      return "EDIT"
    if (userIdInArray(currentUserId, currentOrganization.readAccessUsers))
      return "READ"

    // Default to NONE
    return "NONE"
  }, [currentOrganization, currentUserId, isAdmin, effectiveOrgId, userData])

  // When no specific organization is selected, get all pages the user can access
  const accessiblePages = useMemo(() => {
    // Admin users have access to all pages
    if (isAdmin) return ALL_PAGES

    // Global pages that all users can access regardless of organization
    const globalPages = ["/organizations", "/user-settings"]

    // If no organization is available at all
    if (!effectiveOrgId) return globalPages

    // If we don't have the organization data yet
    if (!currentOrganization) return globalPages

    // If we're in an organization context, return pages based on permission level
    if (permissionLevel === "FULL") {
      return [
        ...filterAdminOnlyPages(
          filterSearchCriteriaPages(ALL_PAGES, searchCriteriaEnabled),
          isAdmin,
        ),
        ...globalPages,
      ]
    }

    // Function to normalize page paths (handles multiple formats, both with and without slashes)
    const normalizePaths = (paths: string[] = []) => {
      if (!paths || !Array.isArray(paths)) {
        return []
      }

      const normalizedPaths = paths
        .flatMap((path) => {
          if (!path) return []

          // Convert title case names (like "Dashboard") to route format ("/dashboard")
          // Handle special cases like "DNC List" -> "/dnc"
          const variations = []

          // Standard lowercase with slash
          const lowercasePath = path.toLowerCase().trim()
          variations.push(
            lowercasePath.startsWith("/") ? lowercasePath : `/${lowercasePath}`,
          )

          // Special case mapping from database format to route format
          const specialCaseMappings: Record<string, string> = {
            Dashboard: "/dashboard",
            Campaigns: "/campaigns",
            Inbox: "/inbox",
            Sequences: "/sequences",
            Senders: "/senders",
            "DNC List": "/dnc",
            "Search Criteria": "/search-criteria",
            "Master Lists": "/master-lists",
            Domains: "/domains",
            "Domains / Inboxes": "/inboxing-domains",
            "Inboxing Domains": "/inboxing-domains",
            Emails: "/emails",
            Prospects: "/prospects",
            "Reply Classification": "/reply-classification",
            "Spam Testing": "/spam-testing",
          }

          // Add special case mapping if it exists
          if (specialCaseMappings[path]) {
            variations.push(specialCaseMappings[path])
          }

          // Handle spaces and dashes
          variations.push(`/${path.toLowerCase().replace(/\s+/g, "-")}`)
          variations.push(`/${path.toLowerCase().replace(/\s+/g, "")}`)

          return variations
        })
        .filter(Boolean) // Remove empty paths

      // Remove duplicates
      const uniquePaths = [...new Set(normalizedPaths)]

      return uniquePaths
    }

    if (permissionLevel === "EDIT") {
      // Default to empty arrays if undefined
      const readPages = currentOrganization.readAccessPages || []
      const editPages = currentOrganization.editAccessPages || []

      // Normalize paths to ensure consistent format
      const normalizedReadPages = normalizePaths(readPages)
      const normalizedEditPages = normalizePaths(editPages)

      // Combine and deduplicate (include global pages)
      const uniquePages = [
        ...new Set([
          ...normalizedReadPages,
          ...normalizedEditPages,
          ...globalPages,
        ]),
      ]
      return filterAdminOnlyPages(
        filterSearchCriteriaPages(uniquePages, searchCriteriaEnabled),
        isAdmin,
      )
    }

    if (permissionLevel === "READ") {
      // Default to empty array if undefined
      const readPages = currentOrganization.readAccessPages || []

      // Normalize paths to ensure consistent format
      const normalizedReadPages = normalizePaths(readPages)

      // Include global pages
      const accessiblePages = filterAdminOnlyPages(
        filterSearchCriteriaPages(
          [...normalizedReadPages, ...globalPages],
          searchCriteriaEnabled,
        ),
        isAdmin,
      )

      return accessiblePages
    }

    return globalPages
  }, [
    currentOrganization,
    isAdmin,
    effectiveOrgId,
    permissionLevel,
    searchCriteriaEnabled,
  ])

  // Check if the user has access to a specific page
  const hasAccessToPage = useMemo(() => {
    if (!props?.page) return true // If no page specified, default to true

    // We've already checked that props.page exists, so we can use it safely
    const page = props.page

    // Function to normalize page name to match database format
    const normalizePageName = (pageName: string): string[] => {
      if (!pageName) return []

      const normalized = pageName.toLowerCase().trim()

      // Remove leading slash for consistent comparison
      const withoutLeadingSlash = normalized.startsWith("/")
        ? normalized.substring(1)
        : normalized

      // Handle cases where the page might be a full path like /organization/123/dashboard
      const pathParts = withoutLeadingSlash.split("/")
      const lastPartOfPath = pathParts[pathParts.length - 1] // Get last segment

      // Create variations with and without leading slash
      const variations = [
        normalized, // As-is
        normalized.startsWith("/") ? normalized : `/${normalized}`, // With leading slash
        withoutLeadingSlash, // Without leading slash
        lastPartOfPath, // Just the last part of the path
        `/${lastPartOfPath}`, // Last part with leading slash
      ]

      return [...new Set(variations)] // Remove duplicates
    }

    // Handle global pages that don't require organization context
    const globalPages = [
      "organizations",
      "user-settings",
      "/organizations",
      "/user-settings",
      "admin",
      "/admin",
      "login",
      "/login",
      "reset-password",
      "/reset-password",
    ]

    // Check if this is a global page
    if (
      globalPages.some(
        (p) =>
          p.toLowerCase() === page.toLowerCase() ||
          p.toLowerCase() === "/" + page.toLowerCase() ||
          page.toLowerCase().includes(p.toLowerCase()),
      )
    ) {
      return true
    }

    // If we're in organization context but don't have the org data yet
    if (effectiveOrgId && !currentOrganization) {
      return false // Wait for data to load
    }

    if (
      !searchCriteriaEnabled &&
      normalizePageName(page).some((pageVar) => isSearchCriteriaPath(pageVar))
    ) {
      return false
    }

    if (!isAdmin && normalizePageName(page).some(isAdminOnlyPath)) {
      return false
    }

    // Admin users have access to all pages
    if (isAdmin) {
      return true
    }

    // Full access users have access to all pages
    if (permissionLevel === "FULL") {
      return true
    }

    // Generate all normalized variations of the page name
    const pageVariations = normalizePageName(page)

    if (
      !searchCriteriaEnabled &&
      pageVariations.some((pageVar) => isSearchCriteriaPath(pageVar))
    ) {
      return false
    }

    // Normalize accessible pages to all possible formats
    const normalizedAccessiblePages = accessiblePages.flatMap((p) =>
      normalizePageName(p),
    )

    // Check if any variation of the page name matches any accessible page
    return pageVariations.some(
      (pageVar) =>
        normalizedAccessiblePages.includes(pageVar) ||
        normalizedAccessiblePages.some((accessiblePath) => {
          // Remove leading slashes to compare parts consistently
          const pageVarWithoutSlash = pageVar.startsWith("/")
            ? pageVar.substring(1)
            : pageVar
          const accessiblePathWithoutSlash = accessiblePath.startsWith("/")
            ? accessiblePath.substring(1)
            : accessiblePath

          // Check if accessiblePath is parent of pageVar
          return pageVarWithoutSlash.startsWith(
            accessiblePathWithoutSlash + "/",
          )
        }),
    )
  }, [
    accessiblePages,
    currentOrganization,
    effectiveOrgId,
    isAdmin,
    permissionLevel,
    props?.page,
    searchCriteriaEnabled,
  ])

  const hasEditPermission = useMemo(() => {
    if (props?.page && !isAdmin && isAdminOnlyPath(props.page)) {
      return false
    }

    if (
      props?.page &&
      isSearchCriteriaPath(props.page) &&
      !searchCriteriaEnabled
    ) {
      return false
    }

    if (permissionLevel === "FULL" || permissionLevel === "EDIT") {
      return true
    }

    if (props?.page === "dnc" || props?.page === "/dnc") {
      return canReadUserEditDnc(currentOrganization, permissionLevel)
    }

    return false
  }, [
    permissionLevel,
    props?.page,
    currentOrganization,
    searchCriteriaEnabled,
    isAdmin,
  ])

  return {
    permissionLevel,
    hasAccessToPage,
    accessiblePages,
    isLoading: effectiveOrgId ? !currentOrganization : false,
    organization: currentOrganization,
    autoSelectedOrgId,
    hasEditPermission,
    hasPageAccess: hasAccessToPage,
    hasEditPermissionForPage: (pageName: string): boolean => {
      if (!isAdmin && isAdminOnlyPath(pageName)) {
        return false
      }

      if (isSearchCriteriaPath(pageName) && !searchCriteriaEnabled) {
        return false
      }

      if (isAdmin || permissionLevel === "FULL") {
        return true
      }

      if (permissionLevel === "READ") {
        if (pageName === "dnc" || pageName === "/dnc") {
          return canReadUserEditDnc(currentOrganization, permissionLevel)
        }
        return false
      }

      if (
        permissionLevel === "EDIT" &&
        currentOrganization &&
        currentOrganization.editAccessPages
      ) {
        // Function to normalize page name to match database format
        const normalizePageName = (pageName: string): string[] => {
          if (!pageName) return []

          const normalized = pageName.toLowerCase().trim()

          // Remove leading slash for consistent comparison
          const withoutLeadingSlash = normalized.startsWith("/")
            ? normalized.substring(1)
            : normalized

          // Handle cases where the page might be a full path like /organization/123/dashboard
          const pathParts = withoutLeadingSlash.split("/")
          const lastPartOfPath = pathParts[pathParts.length - 1] // Get last segment

          // Create variations with and without leading slash
          const variations = [
            normalized, // As-is
            normalized.startsWith("/") ? normalized : `/${normalized}`, // With leading slash
            withoutLeadingSlash, // Without leading slash
            lastPartOfPath, // Just the last part of the path
            `/${lastPartOfPath}`, // Last part with leading slash
          ]

          return [...new Set(variations)] // Remove duplicates
        }

        // Function to normalize page paths (handles multiple formats, both with and without slashes)
        const normalizePaths = (paths: string[] = []): string[] => {
          if (!paths || !Array.isArray(paths)) {
            return []
          }

          const normalizedPaths = paths
            .flatMap((path) => {
              if (!path) return []

              // Convert title case names (like "Dashboard") to route format ("/dashboard")
              // Handle special cases like "DNC List" -> "/dnc"
              const variations = []

              // Standard lowercase with slash
              const lowercasePath = path.toLowerCase().trim()
              variations.push(
                lowercasePath.startsWith("/")
                  ? lowercasePath
                  : `/${lowercasePath}`,
              )

              // Special case mapping from database format to route format
              const specialCaseMappings: Record<string, string> = {
                Dashboard: "/dashboard",
                Campaigns: "/campaigns",
                Inbox: "/inbox",
                Sequences: "/sequences",
                Senders: "/senders",
                "DNC List": "/dnc",
                "Search Criteria": "/search-criteria",
                "Master Lists": "/master-lists",
                Domains: "/domains",
                "Domains / Inboxes": "/inboxing-domains",
                "Inboxing Domains": "/inboxing-domains",
                Emails: "/emails",
                Prospects: "/prospects",
                "Reply Classification": "/reply-classification",
                "Spam Testing": "/spam-testing",
              }

              // Add special case mapping if it exists
              if (specialCaseMappings[path]) {
                variations.push(specialCaseMappings[path])
              }

              // Handle spaces and dashes
              variations.push(`/${path.toLowerCase().replace(/\s+/g, "-")}`)
              variations.push(`/${path.toLowerCase().replace(/\s+/g, "")}`)

              return variations
            })
            .filter(Boolean) // Remove empty paths

          // Remove duplicates
          const uniquePaths = [...new Set(normalizedPaths)]

          return uniquePaths
        }

        const pageVariations = normalizePageName(pageName)
        const normalizedEditPages = normalizePaths(
          currentOrganization.editAccessPages,
        )

        return pageVariations.some(
          (pageVar) =>
            normalizedEditPages.includes(pageVar) ||
            normalizedEditPages.some((editPath) => {
              const pageVarWithoutSlash = pageVar.startsWith("/")
                ? pageVar.substring(1)
                : pageVar
              const editPathWithoutSlash = editPath.startsWith("/")
                ? editPath.substring(1)
                : editPath

              return pageVarWithoutSlash.startsWith(editPathWithoutSlash + "/")
            }),
        )
      }

      return false
    },
  }
}
